import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useSession } from "../../hooks/useSession";
import { usePageMetadata } from "../../hooks/usePageMetadata";
import InputField from "../../components/InputField";
import { TopBar } from "../../components/TopBar";
import { useApiRequest } from "../../hooks/useApiRequest";
import { generatePath, useNavigate } from "react-router-dom";
import { routes } from "../../routes";
import { getOrderStatusDisplayName, Prospect } from "../../types/order";
import { LookupErrorModal } from "./LookupErrorModal";
import { Order } from "../../types/order";
import { formatAsDate } from "../../utils/date";

const BuyerModeRoute: React.FC = () => {
  const { user } = useSession();
  const [lookupField, setLookupField] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const navigate = useNavigate();
  const { data: prospectData, request: prospectRequest } =
    useApiRequest<Prospect>();
  const { request: lookupRequest } =
    useApiRequest<Prospect>();

  usePageMetadata({ title: "Buyer Mode" });

  const handleGetStartedClick = () => {
    prospectRequest("/retailer/addprospect", {
      method: "GET",
    });
  };

  const handleViewHomesClick = () => {
    navigate(
      generatePath(routes.buyerModeHomes.path)
    );
  };

  const handleLookupClick = () => {
    lookupRequest(`/retailer/lookup/${lookupField}`, {
      method: "GET",
    },{
      onSuccess(data: any) {
          if (!data.orderGuid) {
            setShowErrorModal(true);
          } else if (user) {
            navigate(
              generatePath(routes.quotesAndOrdersDetail.path, {
                tenantGuid: user?.tenantGuid,
                orderGuid: data.orderGuid,
              })
            );
          }
      },
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (prospectData && user) {
      navigate(
        generatePath(routes.quotesAndOrdersDetail.path, {
          tenantGuid: user?.tenantGuid,
          orderGuid: prospectData.orderGuid,
        })
      );
    }
  });

  const {
    data: queryData,
    request: queryRequest
  } = useApiRequest<Order[]>();

  useEffect(() => {
    if (lookupField) {
      queryRequest(`/retailer/buyermode/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        data: `query=${encodeURIComponent(lookupField)}`
      });
    } else {
      // somehow clear the data
    }
  },[lookupField, queryRequest]);

  const handleRowClick = (orderGuid: string) => {
    if (user) {
      navigate(
        generatePath(routes.quotesAndOrdersDetail.path, {
          tenantGuid: user?.tenantGuid,
          orderGuid: orderGuid,
        })
      );
    }
  };

  return (
    <>
      <TopBar />
      <Box
        component="main"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "url(/login-background.jpg) no-repeat center center",
          backgroundSize: "cover",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            maxWidth: "1200px",
            width: "100%",
          }}
        >
          {/* Welcome card */}
          <Card
            sx={{
              padding: 3,
              minWidth: 400,
              borderRadius: lookupField ? "16px 0 0 16px" : "16px",
              position: "relative",
              zIndex: 2
            }}
          >
            <Typography
              variant="h5"
              sx={{
                marginBottom: 3,
                textAlign: "center",
                mb: 0,
                fontWeight: 500,
              }}
            >
              Welcome to
            </Typography>
            <Typography
              variant="h5"
              sx={{
                marginBottom: 3,
                textAlign: "center",
                textOverflow: "ellipsis",
                maxWidth: "65vw",
                whiteSpace: "nowrap",
                overflow: "hidden",
                fontWeight: 500,
              }}
            >
              {user?.tenantName}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleGetStartedClick}
            >
              Get Started
            </Button>
            <Divider sx={{ my: 2 }} />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleViewHomesClick}
            >
              View Homes & Purchase Prices
            </Button>
            <Divider sx={{ my: 2 }}>Or find an existing record</Divider>

            <form onSubmit={handleSubmit}>
              <InputField
                sx={{ mt: 1 }}
                placeholder="Enter Order ID or Name"
                fullWidth
                value={lookupField}
                onChange={(e) => setLookupField(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={lookupField.length === 0}
                onClick={handleLookupClick}
              >
                Look Up
              </Button>
            </form>
          </Card>

          {/* Search results table */}
          {lookupField && (
            <Card
              sx={{
                padding: 0,
                borderRadius: "0 16px 16px 0",
                display: "flex",
                overflow: "hidden",
                minHeight: "474px", // Match the height of the welcome card
                borderLeft: "none",
                boxShadow: "none",
                position: "relative",
                zIndex: 1,
                minWidth: 700,
                maxWidth: 700,
              }}
            >
              <TableContainer
                sx={{
                  width: "100%",
                  maxHeight: "474px", // Match the height of the welcome card
                  backgroundColor: "#fff",
                }}
              >
                <Table stickyHeader aria-label="search results table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Buyer</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Mfg Name</TableCell>
                      <TableCell>Model Number</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queryData?.map((order) => (
                      <TableRow
                        key={order.orderGuid}
                        hover
                        onClick={() => handleRowClick(order.orderGuid)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{[order.buyerFirstName, order.buyerLastName].filter(Boolean).join(' ') || ''}</TableCell>
                        <TableCell>{getOrderStatusDisplayName(order.orderStatus)}</TableCell>
                        <TableCell>{formatAsDate(order.createdTimestamp)}</TableCell>
                        <TableCell>{order.orderIdentifier}</TableCell>
                        <TableCell>{order.mfgTenantName || ''}</TableCell>
                        <TableCell>{order.modelNumber || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Box>
      </Box>
      {showErrorModal && (
        <LookupErrorModal onClose={() => setShowErrorModal(false)} />
      )}
    </>
  );
};

export default BuyerModeRoute;